/**
 * GPA Impact Estimation Engine (Phase 2 — Step 2.8)
 *
 * Computes cumulative GPA with and without attendance penalties
 * across multiple courses, accounting for credit-hour weights.
 */
/** Standard 4.0 GPA scale for letter-to-points conversion */
const GPA_SCALE = {
    'A': 4.0, 'A−': 3.7, 'A-': 3.7,
    'B+': 3.3, 'B': 3.0, 'B−': 2.7, 'B-': 2.7,
    'C+': 2.3, 'C': 2.0, 'C−': 1.7, 'C-': 1.7,
    'D+': 1.3, 'D': 1.0, 'D−': 0.7, 'D-': 0.7,
    'F': 0.0,
};
/**
 * Convert a letter grade to GPA points on a 4.0 scale.
 * Returns 0.0 for unrecognized grades.
 */
export function letterGradeToGPA(letter) {
    return GPA_SCALE[letter] ?? 0.0;
}
/**
 * Resolve a numeric grade (0–100) to GPA points using a grading scale.
 */
export function numericGradeToGPA(numericGrade, gradingScale) {
    // Find the letter grade for this numeric value
    const sorted = [...gradingScale].sort((a, b) => b.minPercentage - a.minPercentage);
    for (const entry of sorted) {
        if (numericGrade >= entry.minPercentage) {
            return letterGradeToGPA(entry.letter);
        }
    }
    return 0.0;
}
/**
 * Compute weighted GPA from a list of (gpaPoints, creditHours) pairs.
 */
function computeWeightedGPA(courses) {
    let totalPoints = 0;
    let totalCredits = 0;
    for (const c of courses) {
        totalPoints += c.points * c.credits;
        totalCredits += c.credits;
    }
    if (totalCredits === 0)
        return 0.0;
    return totalPoints / totalCredits;
}
/**
 * Compute the full GPA impact across multiple courses.
 *
 * @param courses - Array of course data with grade impacts
 * @returns GPAImpact with raw GPA, penalized GPA, delta, and per-course breakdown
 */
export function computeGPAImpact(courses) {
    const contributions = [];
    const rawCourses = [];
    const penalizedCourses = [];
    for (const course of courses) {
        if (course.baseGrade === null)
            continue;
        const rawPoints = numericGradeToGPA(course.baseGrade, course.gradingScale);
        const penalizedGrade = course.gradeImpact?.currentGradePoints ?? course.baseGrade;
        const penalizedPoints = numericGradeToGPA(penalizedGrade, course.gradingScale);
        const rawLetter = resolveLetterFromPoints(course.baseGrade, course.gradingScale);
        const penalizedLetter = course.gradeImpact?.currentGrade ?? rawLetter;
        contributions.push({
            courseId: course.courseId,
            courseName: course.courseName,
            rawGrade: rawLetter,
            penalizedGrade: penalizedLetter,
            rawPoints,
            penalizedPoints,
            creditHours: course.creditHours,
        });
        rawCourses.push({ points: rawPoints, credits: course.creditHours });
        penalizedCourses.push({ points: penalizedPoints, credits: course.creditHours });
    }
    const rawGPA = computeWeightedGPA(rawCourses);
    const penalizedGPA = computeWeightedGPA(penalizedCourses);
    const gpaDelta = Math.max(0, rawGPA - penalizedGPA);
    return {
        rawGPA: Math.round(rawGPA * 100) / 100,
        penalizedGPA: Math.round(penalizedGPA * 100) / 100,
        gpaDelta: Math.round(gpaDelta * 100) / 100,
        courseContributions: contributions,
    };
}
/**
 * Resolve a letter grade from numeric points using a grading scale.
 */
function resolveLetterFromPoints(points, scale) {
    const sorted = [...scale].sort((a, b) => b.minPercentage - a.minPercentage);
    for (const entry of sorted) {
        if (points >= entry.minPercentage) {
            return entry.letter;
        }
    }
    return 'F';
}
//# sourceMappingURL=gpaCalculator.js.map